import {useState} from 'react'
import supabase from '../config/supabaseClient';

function BaptismalRegistry() {
    // Simplified state management - removed complex border/color states
    const [showSuccessMessage, setShowSuccessMessage] = useState(false);
    const [currentSection, setCurrentSection] = useState(1); // Start with Child Information
    const [completedSections, setCompletedSections] = useState([]); // Track completed sections
    
    // Form data states
    const [childInfo, setChildInfo] = useState({
        name: "",
        middleName: "",
        lastName: "",
        dateOfBirth: "",
        placeOfBirth: ""
    });
    const [parentsInfo, setParentsInfo] = useState({
        fatherName: "",
        fatherReligion: "",
        motherName: "",
        maidenName: "",
        motherReligion: "",
        parentsMarriedByPriest: true,
        areParentsRegisteredMembersOfTheParish: true
    });
    const [godparentsInfo, setGodparentsInfo] = useState({
        godFatherName: "",
        godFatherReligion: "",
        godMotherName: "",
        godMotherReligion: ""
    });
    const [setDateInfo] = useState({
        preparationClassDate: new Date().toLocaleDateString('en-US', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' }),
        baptismDate: "null"
    });
    const [contactInfo, setContactInfo] = useState({
        email: "",
        phone: ""
    });

    // Simplified section navigation
    const updateCurrentSection = (section, e) => {
        e.preventDefault();
        if(e.target.innerText === "Complete"){
            // Mark current section as completed
            if(!completedSections.includes(section)) {
                setCompletedSections([...completedSections, section]);
            }
            
            // Advance to the next section
            if(section === 2) {
                setCurrentSection(3); // Child -> Parents
            } else if(section === 3) {
                setCurrentSection(4); // Parents -> Godparents
            } else if(section === 4) {
                setCurrentSection(5); // Godparents -> Contact
            } else if(section === 5) {
                setCurrentSection(1); // Contact -> Review
            }
            return;
        }
        // This handles the Edit button clicks
        setCurrentSection(section);
    };

    // Form styles
    const formStyles = {
        container: {
            maxWidth: '800px',
            margin: '0 auto',
            fontFamily: "'Segoe UI', 'Roboto', 'Helvetica Neue', sans-serif"
        },
        header: {
            textAlign: 'center',
            background: 'linear-gradient(135deg, #8fd097 0%, #5a7a5d 100%)',
            padding: '20px',
            color: 'white',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)'
        },
        progressBar: {
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            margin: '20px 0',
            gap: '10px'
        },
        progressStep: {
            width: '40px',
            height: '40px',
            borderRadius: '50%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: 'white',
            fontWeight: 'bold',
            transition: 'all 0.3s ease'
        },
        progressLine: {
            width: '50px',
            height: '4px',
            backgroundColor: '#e0e0e0',
            borderRadius: '2px'
        },
        section: {
            background: 'white',
            padding: '40px',
            boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
            marginBottom: '20px',
            textAlign: 'left'
        },
        sectionTitle: {
            fontSize: '28px',
            fontWeight: '700',
            color: '#333',
            marginBottom: '30px',
            textAlign: 'center',
            position: 'relative'
        },
        inputGroup: {
            marginBottom: '25px'
        },
        label: {
            display: 'block',
            marginBottom: '8px',
            fontWeight: '600',
            color: '#555',
            fontSize: '16px'
        },
        input: {
            width: '90%',
            padding: '15px 20px',
            border: '2px solid #e0e0e0',
            borderRadius: '12px',
            fontSize: '16px',
            transition: 'all 0.3s ease',
            backgroundColor: '#fafafa',
            fontFamily: 'inherit'
        },
        inputFocus: {
            borderColor: '#667eea',
            backgroundColor: 'white',
            boxShadow: '0 0 0 3px rgba(102, 126, 234, 0.1)',
            outline: 'none'
        },
        select: {
            width: '100%',
            padding: '15px 20px',
            border: '2px solid #e0e0e0',
            borderRadius: '12px',
            fontSize: '16px',
            backgroundColor: '#fafafa',
            cursor: 'pointer',
            fontFamily: 'inherit'
        },
        radioGroup: {
            display: 'flex',
            gap: '20px',
            marginTop: '10px'
        },
        radioOption: {
            display: 'flex',
            alignItems: 'center',
            gap: '8px',
            cursor: 'pointer'
        },
        button: {
            background: 'linear-gradient(135deg, #8fd097 0%, #5a7a5d 100%)',
            color: 'white',
            border: 'none',
            padding: '15px 40px',
            borderRadius: '25px',
            fontSize: '18px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            boxShadow: '0 5px 15px rgba(102, 126, 234, 0.3)',
            width: '100%',
            marginTop: '30px'
        },
        buttonHover: {
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)'
        },
        reviewCard: {
            background: 'white',
            padding: '25px',
            borderRadius: '15px',
            boxShadow: '0 5px 15px rgba(0,0,0,0.08)',
            marginBottom: '20px',
            border: '2px solid transparent',
            transition: 'all 0.3s ease'
        },
        reviewCardActive: {
            borderColor: '#5a7a5d',
            transform: 'translateY(-2px)',
            boxShadow: '0 8px 25px rgba(0,0,0,0.12)'
        },
        editButton: {
            background: 'transparent',
            color: '#5a7a5d',
            border: '2px solid #5a7a5d',
            padding: '8px 20px',
            borderRadius: '20px',
            fontSize: '14px',
            fontWeight: '600',
            cursor: 'pointer',
            transition: 'all 0.3s ease',
            marginTop: '15px'
        }
    };

    const renderReligionOptions = (name, value, onChange) => {
        return (
            <select 
                id={name} 
                name={name} 
                value={value} 
                onChange={onChange}
                style={formStyles.select}
            >
                <option value="">Select Religion</option>
                <option value="Catholic">Catholic</option>
                <option value="Christian">Christian</option>
                <option value="Muslim">Muslim</option>
                <option value="Hindu">Hindu</option>
                <option value="Buddhist">Buddhist</option>
                <option value="Other">Other</option>
            </select>
        );
    };

    const registerHandler = async (e) => {
        e.preventDefault();
        console.log("Register Handler was called")
        const requiredFields = {
            "Child Information": {
                "Child Name": childInfo?.name,
                "Date of Birth": childInfo?.dateOfBirth,
                "Place of Birth": childInfo?.placeOfBirth,
            },
            "Parents Information": {
                "Father Name": parentsInfo?.fatherName,
                "Father Religion": parentsInfo?.fatherReligion,
                "Mother Name": parentsInfo?.motherName,
                "Maiden Name": parentsInfo?.maidenName,
                "Mother Religion": parentsInfo?.motherReligion,
            },
            "Contact Information": {
                "Email": contactInfo?.email,
                "Phone": contactInfo?.phone,
            },
        };

        const missingFields = Object.entries(requiredFields).reduce((acc, [section, fields]) => {
            const sectionMissingFields = Object.entries(fields)
                .filter(([, value]) => !value)
                .map(([key]) => `${section} - ${key}`);
            return [...acc, ...sectionMissingFields];
        }, []);

        if (missingFields.length > 0) {
            alert(`Please fill out the following required fields before submitting:\n${missingFields.join(", ")}`);
            return;
        }

        const { data, error } = await supabase
        .from('BaptismalRegistry')
        .insert([
        { 
            email: contactInfo.email,
            form_status: "New",
            phone_number: contactInfo.phone,
            childs_info: {
                full_name: childInfo.name,
                birthday: childInfo.dateOfBirth,
                birth_place: childInfo.placeOfBirth
            },
            parents_info: {
                fathers_name: parentsInfo.fatherName,
                fathers_religion: parentsInfo.fatherReligion,
                mothers_name: parentsInfo.motherName,
                mothers_religion: parentsInfo.motherReligion,
                married_by_priest: parentsInfo.parentsMarriedByPriest,
                registered_members_of_the_parish: parentsInfo.areParentsRegisteredMembersOfTheParish
            },
            godparents_info: {
                godfathers_name: godparentsInfo.godFatherName,
                godfathers_religion: godparentsInfo.godFatherReligion,
                godmothers_name: godparentsInfo.godMotherName,
                godmothers_religion: godparentsInfo.godMotherReligion
            },
            date_info: {
                preparation_date: "null",
                baptism_date: "null"
            },
            registry_status: {
                certificate: false,
                registry: false,
                computer: false
            }
          },
        ])
        .select()
        if (error) {
            console.error('Error inserting data:', error);
            alert("Error inserting data, please try again later.", error.message);
        }
        else {
            console.log('Inserted data:', data);
            setShowSuccessMessage(true);
            // Reset form data and states
            setCurrentSection(2);
            setCompletedSections([]);
            setChildInfo({
                name: "",
                middleName: "",
                lastName: "",
                dateOfBirth: "",
                placeOfBirth: ""
            });
            setParentsInfo({
                fatherName: "",
                fatherReligion: "",
                motherName: "",
                maidenName: "",
                motherReligion: "",
                parentsMarriedByPriest: true,
                areParentsRegisteredMembersOfTheParish: true
            });
            setGodparentsInfo({
                godFatherName: "",
                godFatherReligion: "",
                godMotherName: "",
                godMotherReligion: ""
            });
            setDateInfo({
                preparationClassDate: "null",
                baptismDate: "null"
            });
            setContactInfo({
                email: "",
                phone: ""
            });
        }
    }

    const getProgressStepStyle = (step) => ({
        ...formStyles.progressStep,
        backgroundColor: currentSection === step ? '#667eea' : 
                        completedSections.includes(step) || currentSection === 1 ? '#4CAF50' : '#e0e0e0',
        color: currentSection === step || completedSections.includes(step) || currentSection === 1 ? 'white' : '#999'
    });

    return (
        <div style={formStyles.container}>
            {showSuccessMessage ? (
                <div style={{
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    minHeight: '80vh',
                    padding: '40px 20px',
                    textAlign: 'center'
                }}>
                    <div style={{
                        background: 'linear-gradient(135deg, #67886A 0%, #5a7a5d 100%)',
                        padding: '50px 40px',
                        borderRadius: '20px',
                        boxShadow: '0 20px 40px rgba(0,0,0,0.1), 0 0 0 1px rgba(255,255,255,0.1)',
                        color: 'white',
                        maxWidth: '600px',
                        width: '100%',
                        position: 'relative',
                        overflow: 'hidden'
                    }}>
                        <div style={{
                            position: 'absolute',
                            top: '-50px',
                            right: '-50px',
                            width: '100px',
                            height: '100px',
                            background: 'rgba(255,255,255,0.1)',
                            borderRadius: '50%',
                            filter: 'blur(40px)'
                        }}></div>
                        
                        <div style={{
                            fontSize: '4rem',
                            marginBottom: '20px',
                            color: '#90EE90'
                        }}>✓</div>
                        
                        <h1 style={{
                            fontSize: '2.5rem',
                            fontWeight: '700',
                            marginBottom: '20px',
                            color: 'white'
                        }}>Thank You!</h1>
                        
                        <p style={{
                            fontSize: '1.3rem',
                            lineHeight: '1.6',
                            marginBottom: '30px',
                            color: 'rgba(255,255,255,0.9)'
                        }}>
                            Your baptism request has been successfully submitted and will be reviewed by 
                            <strong style={{color: '#90EE90'}}> Deacon Tom</strong>.
                        </p>
                        {/* contact information */}
                        <div style={{
                            background: 'rgba(255,255,255,0.1)',
                            padding: '25px',
                            borderRadius: '15px',
                            marginBottom: '30px',
                            backdropFilter: 'blur(10px)'
                        }}>
                            <h3 style={{
                                fontSize: '1.4rem',
                                marginBottom: '15px',
                                color: '#90EE90'
                            }}>Contact Information</h3>
                            
                            <div style={{
                                display: 'flex',
                                flexDirection: 'column',
                                gap: '10px'
                            }}>
                                <p style={{
                                    fontSize: '1.1rem',
                                    margin: '0',
                                    color: 'rgba(255,255,255,0.9)'
                                }}>
                                    📱 Text: <strong>931-241-0910</strong>
                                </p>
                                <p style={{
                                    fontSize: '1.1rem',
                                    margin: '0',
                                    color: 'rgba(255,255,255,0.9)'
                                }}>
                                    📧 Email: <strong>ttorson@aol.com</strong>
                                </p>
                            </div>
                            {/* button to save contact information to user phone */}
                            <button
                                onClick={() => {
                                    // Implement functionality to save contact information to user's phone
                                    alert("This feature is coming soon! In the meantime, please save our contact information manually.");
                                }}
                                style={{
                                    background: '#8dbe92',
                                    color: 'white',
                                    border: 'none',
                                    padding: '10px 20px',
                                    borderRadius: '5px',
                                    fontSize: '1rem',
                                    cursor: 'pointer',
                                    transition: 'all 0.3s ease'
                                }}
                                onMouseOver={(e) => {
                                    e.target.style.background = '#78d078';
                                }}
                                onMouseOut={(e) => {
                                    e.target.style.background = '#90EE90';
                                }}
                            >
                                Save Contact Info
                            </button>
                        </div>
                        
                        <button 
                            onClick={() => {
                                setShowSuccessMessage(false);
                                setCurrentSection(2);
                                setCompletedSections([]);
                            }}
                            style={{
                                background: 'white',
                                color: '#67886A',
                                border: 'none',
                                padding: '15px 30px',
                                borderRadius: '50px',
                                fontSize: '1.1rem',
                                fontWeight: '600',
                                cursor: 'pointer',
                                transition: 'all 0.3s ease',
                                boxShadow: '0 5px 15px rgba(0,0,0,0.2)'
                            }}
                            onMouseOver={(e) => {
                                e.target.style.transform = 'translateY(-2px)';
                                e.target.style.boxShadow = '0 8px 25px rgba(0,0,0,0.3)';
                            }}
                            onMouseOut={(e) => {
                                e.target.style.transform = 'translateY(0)';
                                e.target.style.boxShadow = '0 5px 15px rgba(0,0,0,0.2)';
                            }}
                        >
                            Submit Another Request
                        </button>
                    </div>
                </div>
            ) : (
                <>
                    <header style={formStyles.header}>
                        <h1 style={{fontSize: '36px', margin: '0 0 10px 0', fontWeight: '700'}}>
                            Baptismal Registry
                        </h1>
                        <p style={{fontSize: '18px', margin: '0', opacity: '0.9'}}>
                            Complete your baptism registration in 4 simple steps
                        </p>
                        
                        {/* Progress Bar */}
                        <div style={formStyles.progressBar}>
                            <div style={getProgressStepStyle(2)}>1</div>
                            <div style={formStyles.progressLine}></div>
                            <div style={getProgressStepStyle(3)}>2</div>
                            <div style={formStyles.progressLine}></div>
                            <div style={getProgressStepStyle(4)}>3</div>
                            <div style={formStyles.progressLine}></div>
                            <div style={getProgressStepStyle(5)}>4</div>
                        </div>
                        
                        <div style={{marginTop: '20px', fontSize: '18px', fontWeight: '600'}}>
                            {currentSection === 2 && "Child Information"}
                            {currentSection === 3 && "Parents Information"}
                            {currentSection === 4 && "Godparents Information"}
                            {currentSection === 5 && "Contact Information"}
                            {currentSection === 1 && "Review & Submit"}
                        </div>
                    </header>
                    
                    <form onSubmit={registerHandler}>
                        {/* Child Information */}
                        {currentSection === 2 && (
                            <div style={formStyles.section}>
                                <h2 style={formStyles.sectionTitle}>👶 Child Information</h2>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Full Name *</label>
                                    <input 
                                        type="text" 
                                        placeholder="Enter child's full name"
                                        style={formStyles.input}
                                        value={childInfo.name} 
                                        onChange={(e) => setChildInfo({...childInfo, name: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Date of Birth *</label>
                                    <input 
                                        type="date" 
                                        style={formStyles.input}
                                        value={childInfo.dateOfBirth} 
                                        onChange={(e) => setChildInfo({...childInfo, dateOfBirth: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Place of Birth *</label>
                                    <input 
                                        type="text" 
                                        placeholder="City, State"
                                        style={formStyles.input}
                                        value={childInfo.placeOfBirth} 
                                        onChange={(e) => setChildInfo({...childInfo, placeOfBirth: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <button 
                                    type="button"
                                    style={formStyles.button} 
                                    onClick={(e) => updateCurrentSection(2,e)}
                                    onMouseOver={(e) => Object.assign(e.target.style, formStyles.buttonHover)}
                                    onMouseOut={(e) => e.target.style.transform = 'none'}
                                >
                                    Complete
                                </button>
                            </div>
                        )}

                        {/* Parents Information */}
                        {currentSection === 3 && (
                            <div style={formStyles.section}>
                                <h2 style={formStyles.sectionTitle}>👪 Parents Information</h2>
                                
                                <h3 style={{color: '#667eea', fontSize: '20px', marginBottom: '20px'}}>Father's Information</h3>
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Full Name *</label>
                                    <input 
                                        type="text" 
                                        placeholder="Father's full name"
                                        style={formStyles.input}
                                        value={parentsInfo.fatherName} 
                                        onChange={(e) => setParentsInfo({...parentsInfo, fatherName: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Religion *</label>
                                    {renderReligionOptions("fatherReligion", parentsInfo.fatherReligion, (e) => setParentsInfo({...parentsInfo, fatherReligion: e.target.value}))}
                                </div>

                                <h3 style={{color: '#667eea', fontSize: '20px', marginBottom: '20px', marginTop: '30px'}}>Mother's Information</h3>
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Full Name *</label>
                                    <input 
                                        type="text" 
                                        placeholder="Mother's full name"
                                        style={formStyles.input}
                                        value={parentsInfo.motherName} 
                                        onChange={(e) => setParentsInfo({...parentsInfo, motherName: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Maiden Name *</label>
                                    <input 
                                        type="text" 
                                        placeholder="Mother's maiden name"
                                        style={formStyles.input}
                                        value={parentsInfo.maidenName} 
                                        onChange={(e) => setParentsInfo({...parentsInfo, maidenName: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Religion *</label>
                                    {renderReligionOptions("motherReligion", parentsInfo.motherReligion, (e) => setParentsInfo({...parentsInfo, motherReligion: e.target.value}))}
                                </div>

                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Were parents married by a priest?</label>
                                    <div style={formStyles.radioGroup}>
                                        <label style={formStyles.radioOption}>
                                            <input 
                                                type="radio" 
                                                name="parentsMarriedByPriest" 
                                                checked={parentsInfo.parentsMarriedByPriest === true} 
                                                onChange={() => setParentsInfo({...parentsInfo, parentsMarriedByPriest: true})} 
                                            />
                                            Yes
                                        </label>
                                        <label style={formStyles.radioOption}>
                                            <input 
                                                type="radio" 
                                                name="parentsMarriedByPriest" 
                                                checked={parentsInfo.parentsMarriedByPriest === false} 
                                                onChange={() => setParentsInfo({...parentsInfo, parentsMarriedByPriest: false})} 
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>

                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Are parents registered members of the parish?</label>
                                    <div style={formStyles.radioGroup}>
                                        <label style={formStyles.radioOption}>
                                            <input 
                                                type="radio" 
                                                name="areParentsRegistered" 
                                                checked={parentsInfo.areParentsRegisteredMembersOfTheParish === true} 
                                                onChange={() => setParentsInfo({...parentsInfo, areParentsRegisteredMembersOfTheParish: true})} 
                                            />
                                            Yes
                                        </label>
                                        <label style={formStyles.radioOption}>
                                            <input 
                                                type="radio" 
                                                name="areParentsRegistered" 
                                                checked={parentsInfo.areParentsRegisteredMembersOfTheParish === false} 
                                                onChange={() => setParentsInfo({...parentsInfo, areParentsRegisteredMembersOfTheParish: false})} 
                                            />
                                            No
                                        </label>
                                    </div>
                                </div>
                                
                                <button 
                                    type="button"
                                    style={formStyles.button} 
                                    onClick={(e) => updateCurrentSection(3,e)}
                                    onMouseOver={(e) => Object.assign(e.target.style, formStyles.buttonHover)}
                                    onMouseOut={(e) => e.target.style.transform = 'none'}
                                >
                                    Complete
                                </button>
                            </div>
                        )}

                        {/* Godparents Information */}
                        {currentSection === 4 && (
                            <div style={formStyles.section}>
                                <h2 style={formStyles.sectionTitle}>🙏 Godparents Information</h2>
                                
                                <h3 style={{color: '#667eea', fontSize: '20px', marginBottom: '20px'}}>Godfather</h3>
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Full Name</label>
                                    <input 
                                        type="text" 
                                        placeholder="Godfather's full name"
                                        style={formStyles.input}
                                        value={godparentsInfo.godFatherName} 
                                        onChange={(e) => setGodparentsInfo({...godparentsInfo, godFatherName: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Religion</label>
                                    {renderReligionOptions("godFatherReligion", godparentsInfo.godFatherReligion, (e) => setGodparentsInfo({...godparentsInfo, godFatherReligion: e.target.value}))}
                                </div>

                                <h3 style={{color: '#667eea', fontSize: '20px', marginBottom: '20px', marginTop: '30px'}}>Godmother</h3>
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Full Name</label>
                                    <input 
                                        type="text" 
                                        placeholder="Godmother's full name"
                                        style={formStyles.input}
                                        value={godparentsInfo.godMotherName} 
                                        onChange={(e) => setGodparentsInfo({...godparentsInfo, godMotherName: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Religion</label>
                                    {renderReligionOptions("godMotherReligion", godparentsInfo.godMotherReligion, (e) => setGodparentsInfo({...godparentsInfo, godMotherReligion: e.target.value}))}
                                </div>
                                
                                <button 
                                    type="button"
                                    style={formStyles.button} 
                                    onClick={(e) => updateCurrentSection(4,e)}
                                    onMouseOver={(e) => Object.assign(e.target.style, formStyles.buttonHover)}
                                    onMouseOut={(e) => e.target.style.transform = 'none'}
                                >
                                    Complete
                                </button>
                            </div>
                        )}

                        {/* Contact Information */}
                        {currentSection === 5 && (
                            <div style={formStyles.section}>
                                <h2 style={formStyles.sectionTitle}>📞 Contact Information</h2>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Email Address *</label>
                                    <input 
                                        type="email" 
                                        placeholder="your.email@example.com"
                                        style={formStyles.input}
                                        value={contactInfo.email} 
                                        onChange={(e) => setContactInfo({...contactInfo, email: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <div style={formStyles.inputGroup}>
                                    <label style={formStyles.label}>Phone Number *</label>
                                    <input 
                                        type="tel" 
                                        placeholder="(555) 123-4567"
                                        style={formStyles.input}
                                        value={contactInfo.phone} 
                                        onChange={(e) => setContactInfo({...contactInfo, phone: e.target.value})} 
                                        onFocus={(e) => Object.assign(e.target.style, formStyles.inputFocus)}
                                        onBlur={(e) => e.target.style.borderColor = '#e0e0e0'}
                                    />
                                </div>
                                
                                <button 
                                    type="button"
                                    style={formStyles.button} 
                                    onClick={(e) => updateCurrentSection(5,e)}
                                    onMouseOver={(e) => Object.assign(e.target.style, formStyles.buttonHover)}
                                    onMouseOut={(e) => e.target.style.transform = 'none'}
                                >
                                    Complete
                                </button>
                            </div>
                        )}

                        {/* Review Section */}
                                            {/* Review Section */}
                        {currentSection === 1 && (
                            <div>
                                <div style={{
                                    background: 'white',
                                    boxShadow: '0 10px 30px rgba(0,0,0,0.1)',
                                    marginBottom: '30px'
                                }}>
                                    <h2 style={{
                                        fontSize: '28px',
                                        fontWeight: '700',
                                        color: '#333',
                                        marginBottom: '30px',
                                        textAlign: 'center'
                                    }}>📋 Baptism Information</h2>
                                    
                                    <div style={{
                                        display: 'grid',
                                        gap: '15px',
                                        marginBottom: '30px'
                                    }}>
                                        {/* Compact Child Information */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '15px 20px',
                                            background: '#f8f9ff',
                                            borderRadius: '12px',
                                            border: '2px solid #e8f1ff'
                                        }}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <span style={{fontSize: '24px'}}>👶</span>
                                                <div>
                                                    <h4 style={{margin: '0', fontSize: '16px', fontWeight: '600', color: '#333'}}>
                                                        Child Information
                                                    </h4>
                                                    <p style={{margin: '0', fontSize: '14px', color: '#666'}}>
                                                        {childInfo.name ? `${childInfo.name} - ${childInfo.dateOfBirth}` : 'Not completed'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button 
                                                type="button"
                                                style={{
                                                    background: 'transparent',
                                                    color: '#5a7a5d',
                                                    border: '2px solid #5a7a5d',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={(e) => updateCurrentSection(2,e)}
                                                onMouseOver={(e) => {
                                                    e.target.style.background = '#5a7a5d';
                                                    e.target.style.color = 'white';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.color = '#5a7a5d';
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>

                                        {/* Compact Parents Information */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '15px 20px',
                                            background: '#f8f9ff',
                                            borderRadius: '12px',
                                            border: '2px solid #e8f1ff'
                                        }}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <span style={{fontSize: '24px'}}>👪</span>
                                                <div>
                                                    <h4 style={{margin: '0', fontSize: '16px', fontWeight: '600', color: '#333'}}>
                                                        Parents Information
                                                    </h4>
                                                    <p style={{margin: '0', fontSize: '14px', color: '#666'}}>
                                                        {parentsInfo.fatherName && parentsInfo.motherName ? 
                                                            `${parentsInfo.fatherName} & ${parentsInfo.motherName}` : 
                                                            'Not completed'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button 
                                                type="button"
                                                style={{
                                                    background: 'transparent',
                                                    color: '#5a7a5d',
                                                    border: '2px solid #5a7a5d',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={(e) => updateCurrentSection(3,e)}
                                                onMouseOver={(e) => {
                                                    e.target.style.background = '#5a7a5d';
                                                    e.target.style.color = 'white';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.color = '#5a7a5d';
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>

                                        {/* Compact Godparents Information */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '15px 20px',
                                            background: '#f8f9ff',
                                            borderRadius: '12px',
                                            border: '2px solid #e8f1ff'
                                        }}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <span style={{fontSize: '24px'}}>🙏</span>
                                                <div>
                                                    <h4 style={{margin: '0', fontSize: '16px', fontWeight: '600', color: '#333'}}>
                                                        Godparents Information
                                                    </h4>
                                                    <p style={{margin: '0', fontSize: '14px', color: '#666'}}>
                                                        {godparentsInfo.godFatherName || godparentsInfo.godMotherName ? 
                                                            `${godparentsInfo.godFatherName || 'No godfather'} & ${godparentsInfo.godMotherName || 'No godmother'}` : 
                                                            'Optional - Not specified'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button 
                                                type="button"
                                                style={{
                                                    background: 'transparent',
                                                    color: '#5a7a5d',
                                                    border: '2px solid #5a7a5d',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={(e) => updateCurrentSection(4,e)}
                                                onMouseOver={(e) => {
                                                    e.target.style.background = '#5a7a5d';
                                                    e.target.style.color = 'white';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.color = '#5a7a5d';
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>

                                        {/* Compact Contact Information */}
                                        <div style={{
                                            display: 'flex',
                                            alignItems: 'center',
                                            justifyContent: 'space-between',
                                            padding: '15px 20px',
                                            background: '#f8f9ff',
                                            borderRadius: '12px',
                                            border: '2px solid #e8f1ff'
                                        }}>
                                            <div style={{display: 'flex', alignItems: 'center', gap: '15px'}}>
                                                <span style={{fontSize: '24px'}}>📞</span>
                                                <div>
                                                    <h4 style={{margin: '0', fontSize: '16px', fontWeight: '600', color: '#333'}}>
                                                        Contact Information
                                                    </h4>
                                                    <p style={{margin: '0', fontSize: '14px', color: '#666'}}>
                                                        {contactInfo.email ? 
                                                            `${contactInfo.email} - ${contactInfo.phone}` : 
                                                            'Not completed'}
                                                    </p>
                                                </div>
                                            </div>
                                            <button 
                                                type="button"
                                                style={{
                                                    background: 'transparent',
                                                    color: '#5a7a5d',
                                                    border: '2px solid #5a7a5d',
                                                    padding: '8px 16px',
                                                    borderRadius: '20px',
                                                    fontSize: '14px',
                                                    fontWeight: '600',
                                                    cursor: 'pointer',
                                                    transition: 'all 0.3s ease'
                                                }}
                                                onClick={(e) => updateCurrentSection(5,e)}
                                                onMouseOver={(e) => {
                                                    e.target.style.background = '#5a7a5d';
                                                    e.target.style.color = 'white';
                                                }}
                                                onMouseOut={(e) => {
                                                    e.target.style.background = 'transparent';
                                                    e.target.style.color = '#5a7a5d';
                                                }}
                                            >
                                                Edit
                                            </button>
                                        </div>
                                    </div>

                                    {/* Prominent Submit Button */}
                                    <div style={{
                                        textAlign: 'center',
                                        padding: '20px',
                                        background: 'linear-gradient(135deg, #8fd097 0%, #5a7a5d 100%)',
                                    }}>
                                        <h3 style={{
                                            color: 'white',
                                            margin: '0 0 15px 0',
                                            fontSize: '20px',
                                            fontWeight: '600'
                                        }}>
                                            Ready to Submit? 🎉
                                        </h3>
                                        <p style={{
                                            color: 'rgba(255,255,255,0.9)',
                                            margin: '0 0 20px 0',
                                            fontSize: '14px'
                                        }}>
                                            Please review your information above before submitting
                                        </p>
                                        <button 
                                            type="submit" 
                                            style={{
                                                background: '#81ba87',
                                                color: 'white',
                                                border: 'none',
                                                padding: '18px 50px',
                                                borderRadius: '50px',
                                                fontSize: '18px',
                                                fontWeight: '700',
                                                cursor: 'pointer',
                                                transition: 'all 0.3s ease',
                                                boxShadow: '0 5px 20px rgba(0,0,0,0.2)',
                                                textTransform: 'uppercase',
                                                letterSpacing: '1px'
                                            }}
                                            onMouseOver={(e) => {
                                                e.target.style.transform = 'translateY(-3px)';
                                                e.target.style.boxShadow = '0 10px 30px rgba(0,0,0,0.3)';
                                                e.target.style.background = '#5a7a5d';
                                            }}
                                            onMouseOut={(e) => {
                                                e.target.style.transform = 'translateY(0)';
                                                e.target.style.boxShadow = '0 5px 20px rgba(0,0,0,0.2)';
                                                e.target.style.background = '#81ba87';
                                            }}
                                        >
                                            Submit Registration
                                        </button>
                                    </div>
                                </div>
                            </div>
                        )}
                    </form>
                </>
            )}
        </div>
    );
}

export default BaptismalRegistry